/* global R7Insight */
import 'react-app-polyfill/stable';

// Import Sentry first for error tracking
import './sentry';
import { history } from './sentry';
import * as Sentry from '@sentry/react';

import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/es/integration/react';
import { TouchBackend } from 'react-dnd-touch-backend';
import { DndProvider } from 'react-dnd';
import { SnackbarProvider } from 'notistack';
import axios from 'axios';
import { v4 as uuidV4 } from 'uuid';
import get from 'lodash.get';

import './semantic/dist/semantic.min.css';
import './index.scss';

import App from './App';
import configureStore from './configureStore';
import * as serviceWorker from './serviceWorker';

// Components
import SplashScreen from './components/SplashScreen';

// History is imported from sentry.ts
const { persistor, store } = configureStore(history);

// Environment
const { REACT_APP_VERSION } = process.env;
// eslint-disable-next-line no-console
console.log(REACT_APP_VERSION);

// A request interceptor to attach uuid for logging
axios.interceptors.request.use(
    (config) => {
        // eslint-disable-next-line no-param-reassign
        config.headers['x-request-id'] = uuidV4();
        return config;
    },
    (error) => {
        Sentry.captureException(error, {
            tags: { error_type: 'axios_request_error' }
        });
        return Promise.reject(error);
    }
);

// A response interceptor for error logging
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const xRequestId = get(error, 'config.headers.x-request-id');

        // Create breadcrumb for the failed request
        Sentry.addBreadcrumb({
            category: 'http',
            message: `HTTP ${error.config?.method?.toUpperCase()} ${
                error.config?.url
            }`,
            level: 'error',
            data: {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response?.status,
                requestId: xRequestId
            }
        });

        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            Sentry.captureException(error, {
                tags: {
                    error_type: 'api_response_error',
                    status_code: error.response.status,
                    request_id: xRequestId
                },
                contexts: {
                    response: {
                        status: error.response.status,
                        statusText: error.response.statusText
                    },
                    request: {
                        url: error.config?.url,
                        method: error.config?.method
                    }
                },
                level: error.response.status >= 500 ? 'error' : 'warning'
            });

            R7Insight.warn(
                xRequestId,
                'The request was made and the server responded with a status code that falls out of the range of 2xx',
                `${error}`
            );
        } else if (error.request) {
            // The request was made but no response was received
            Sentry.captureException(error, {
                tags: {
                    error_type: 'api_no_response',
                    request_id: xRequestId
                },
                level: 'warning'
            });

            R7Insight.warn(
                xRequestId,
                'The request was made but no response was received',
                `${error}`
            );
        } else {
            // Something happened in setting up the request that triggered an Error
            Sentry.captureException(error, {
                tags: {
                    error_type: 'api_setup_error',
                    request_id: xRequestId
                }
            });

            R7Insight.warn(
                xRequestId,
                'Something happened in setting up the request that triggered an Error',
                `${error}`
            );
        }

        return Promise.reject(error);
    }
);

/**
 * Polyfill for browsers that don't support Document.elementsFromPoint()
 * {@link https://github.com/react-dnd/react-dnd/issues/1590#issuecomment-552480754}
 */
function getDropTargetElementsAtPoint(x, y, dropTargets) {
    if (document.msElementsFromPoint) {
        return document.msElementsFromPoint(x, y);
    }
    return dropTargets.filter((t) => {
        const rect = t.getBoundingClientRect();
        return (
            x >= rect.left &&
            x <= rect.right &&
            y <= rect.bottom &&
            y >= rect.top
        );
    });
}

// Check if browser supports Document.elementsFromPoint()
const hasNativeElementsFromPoint = document && document.elementsFromPoint;

ReactDOM.render(
    <React.Fragment>
        <DndProvider
            backend={TouchBackend}
            options={{
                enableMouseEvents: true,
                enableTouchEvents: true,
                getDropTargetElementsAtPoint:
                    !hasNativeElementsFromPoint && getDropTargetElementsAtPoint
            }}
        >
            <Provider store={store}>
                <SnackbarProvider
                    maxSnack={1}
                    hideIconVariant
                    autoHideDuration={3000}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right'
                    }}
                    classes={{
                        base: 'snackbar',
                        variantSuccess: 'snackbar-success',
                        variantError: 'snackbar-error',
                        variantWarning: 'snackbar-warning',
                        variantInfo: 'snackbar-info'
                    }}
                >
                    <PersistGate
                        loading={<SplashScreen />}
                        persistor={persistor}
                    >
                        <App history={history} />
                    </PersistGate>
                </SnackbarProvider>
            </Provider>
        </DndProvider>
    </React.Fragment>,
    document.getElementById('root')
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
